import React, { useState, useEffect } from 'react';
import { FileText, Users, Globe, Shield, Scale, CreditCard, AlertCircle, Lock, Gavel } from 'lucide-react';
import { MarketingLayout } from '../components/shell/MarketingLayout';
import { LegalHero } from '../components/shell/LegalHero';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';

export const TermsPage = () => {
  const brandName = 'OrgSets';
  const proseClasses = "prose prose-lg max-w-none leading-relaxed prose-headings:font-black prose-headings:tracking-tight prose-p:text-black prose-li:text-black prose-strong:text-black dark:prose-headings:text-white dark:prose-p:text-white dark:prose-li:text-white dark:prose-strong:text-white";

  const [activeSection, setActiveSection] = useState('acceptance');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['acceptance', 'user-accounts', 'platform-usage', 'payments', 'third-party', 'liability', 'governing-law', 'user-content', 'age-requirements'];
      let current = sections[0];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) {
            current = section;
          }
        }
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
        current = sections[sections.length - 1];
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'acceptance', label: '1. Acceptance of Terms' },
    { id: 'user-accounts', label: '2. Your Account' },
    { id: 'platform-usage', label: '3. Platform Usage' },
    { id: 'payments', label: '4. Payments & Subscriptions' },
    { id: 'third-party', label: '5. Third-Party Services' },
    { id: 'liability', label: '6. Liability' },
    { id: 'governing-law', label: '7. Governing Law' },
    { id: 'user-content', label: '8. User Content' },
    { id: 'age-requirements', label: '9. Age Requirements' },
  ];

  return (
    <MarketingLayout mainClassName="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
      <SEO title="Terms of Service" description={`Read the terms of service for using ${brandName}.`} />

      <div className="max-w-6xl mx-auto">
          
          {/* Hero Section */}
          <LegalHero 
            title="Terms of Service"
            subtitle={`Please read these terms carefully before using ${brandName}. These rules ensure a safe and reliable environment for everyone.`}
            icon={<Scale size={14} />}
            label="Legal Agreement"
            version="1.1"
            updated="June 2026"
          />

          <div className="flex flex-col md:flex-row gap-12 lg:gap-24 relative">
            
            {/* Sticky Navigation */}
            <aside className="md:w-64 flex-shrink-0 hidden md:block">
              <div className="sticky top-32 space-y-6">
                <h4 className="font-bold uppercase text-[10px] tracking-widest font-mono text-slate-400 dark:text-slate-500">
                  On this page
                </h4>
                <nav className="flex flex-col gap-3 relative before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-slate-200 dark:before:bg-slate-800">
                  {navItems.map((item) => (
                    <a 
                      key={item.id}
                      href={`#${item.id}`} 
                      className={cn(
                        "pl-4 py-1 text-sm font-medium transition-all duration-200 relative",
                        activeSection === item.id 
                          ? "text-brand-primary before:absolute before:inset-y-0 before:-left-px before:w-[2px] before:bg-brand-primary" 
                          : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                      )}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1 max-w-3xl space-y-20">
              
              <section id="acceptance" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                    <Gavel size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">1. The Ground Rules</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    By accessing or using {brandName}, you agree to be bound by these Terms of Service. If you disagree with any part of the terms, you may not access the service. We may modify these terms at any time. For material changes, we will notify you via email or platform notification at least 30 days before they take effect.
                  </p>
                </div>
              </section>

              <section id="user-accounts" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                    <Lock size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">2. Your Account</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. We cannot and will not be liable for any loss or damage arising from your failure to comply with these security obligations.
                  </p>
                  <p>
                    Identity and authentication on {brandName} are securely managed by <strong>Clerk</strong>. By creating an account, you also agree to Clerk's terms regarding data handling for authentication purposes.
                  </p>
                </div>
              </section>

              <section id="platform-usage" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-teal-500/10 flex items-center justify-center text-teal-500">
                    <AlertCircle size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">3. Platform Usage</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    We grant you a personal, non-transferable, non-exclusive license to use {brandName} for managing your organization. You agree not to use the platform for any illegal activities, spam, harassment, or to distribute malicious software.
                  </p>
                  <p>
                    Violation of these rules may result in immediate termination of your account and access to the Service at our sole discretion.
                  </p>
                </div>
              </section>

              <section id="payments" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                    <CreditCard size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">4. Payments & Subscriptions</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    Some parts of the Service are billed on a subscription basis. You will be billed in advance on a recurring schedule. 
                  </p>
                  <div className="my-6 p-6 rounded-xl border bg-rose-50 border-rose-100 dark:bg-rose-500/10 dark:border-rose-500/20">
                    <h3 className="text-lg font-bold mb-2 text-rose-700 dark:text-rose-400">Refund & Cancellation Policy</h3>
                    <p className="m-0 text-rose-800/80 dark:text-rose-200/80">
                      <strong>All subscription payments are final and non-refundable.</strong> If you choose to cancel your subscription, your cancellation will take effect at the end of your current billing cycle. You will continue to have full access to your plan's features until the end of that month, but no prorated refunds will be issued for unused time.
                    </p>
                  </div>
                  <p>
                    Payment processing, dues collection, and ledger splits are handled entirely by <strong>Stripe, Inc.</strong> By initiating payments or collecting funds through {brandName}, you are also bound by the <a href="https://stripe.com/legal/connect-account" target="_blank" rel="noreferrer" className="text-brand-primary hover:underline font-semibold transition-colors">Stripe Connected Account Agreement</a> and Stripe's general Terms of Service.
                  </p>
                  <p>
                    We do not directly store your credit card information on our servers; all sensitive financial data is tokenized and vaulted by Stripe.
                  </p>
                </div>
              </section>

              <section id="third-party" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-500">
                    <Globe size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">5. Third-Party Services</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    To provide a world-class experience, {brandName} integrates with industry-leading third-party services:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mt-4 marker:text-brand-primary">
                    <li><strong>Clerk:</strong> For secure user identity management and authentication.</li>
                    <li><strong>Stripe:</strong> For secure payment processing, dues collection, and financial ledger routing.</li>
                  </ul>
                  <p className="mt-4">
                    Your interactions with these services are governed by their respective privacy policies and terms of service. We are not responsible for the practices of these third parties.
                  </p>
                </div>
              </section>

              <section id="liability" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-rose-500/10 flex items-center justify-center text-rose-500">
                    <Shield size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">6. Limitation of Liability</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    In no event shall {brandName}, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
                  </p>
                  <p>
                    The Service is provided on an "AS IS" and "AS AVAILABLE" basis. We make no warranties regarding the reliability or availability of the platform.
                  </p>
                </div>
              </section>

              <section id="governing-law" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-slate-500/10 flex items-center justify-center text-slate-500">
                    <Scale size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">7. Governing Law & Dispute Resolution</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    These Terms shall be governed by the laws of the State of Delaware, without regard to its conflict of law provisions. Any disputes arising out of or relating to these Terms or the Service shall be resolved exclusively in the state or federal courts located in Delaware.
                  </p>
                  <p>
                    <strong>Binding Arbitration & Class Action Waiver:</strong> You agree that any dispute, claim, or controversy arising out of or relating to these Terms or the breach, termination, enforcement, interpretation, or validity thereof, shall be determined by binding arbitration. You explicitly waive your right to participate in a class action lawsuit or class-wide arbitration.
                  </p>
                </div>
              </section>

              <section id="user-content" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500">
                    <FileText size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">8. User Content License</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    You retain ownership of any content you upload or transmit through {brandName}. However, by posting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, display, and distribute your content strictly for the purpose of operating, improving, and providing the Service to you and your organization.
                  </p>
                </div>
              </section>

              <section id="age-requirements" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 flex items-center justify-center text-cyan-500">
                    <Users size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">9. Age Requirements</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    Our Service is not intended for users under the age of 13 (or 16 in the European Union). By using {brandName}, you state that you meet these minimum age requirements. We do not knowingly collect personal information from children under these ages.
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
    </MarketingLayout>
  );
};
